<template>
  <div class="content flex my-4 relative">
    <div class="auth-form mx-auto my-auto">
      <div class="card">
        <div class="card-body">
          <h1 class="heading-big mb-4">
            {{ $t('register.registration') }}
          </h1>
          <f-alert
            v-if="authError"
            theme="danger"
            visible
            class="mt-4 mb-5">
            <template v-if="currentErrors && Object.keys(currentErrors).length">
              <div>
                <template
                  v-for="(errorKey) in Object.keys(currentErrors)">
                  <div
                    v-for="(errorItem, errorItemIndex) in currentErrors[errorKey]"
                    :key="'error-' + errorKey + '-' + errorItemIndex"
                    class="mb-1 error-item">
                    {{ errorItem }}
                  </div>
                </template>
              </div>
            </template>
            <template v-else>
              {{ $t('register.registrationError') }}
            </template>
          </f-alert>

          <div
            v-if="!authError && !allDataLoaded"
            class="loading-overlay">
            <div class="loading-spinner-wrapper is-embeded">
              <div class="loading-spinner pb-3 pt-3">
                <span class="loading-spinner-content">
                  {{ $t('ui.loadingDataInProgress') }}&hellip;
                </span>
              </div>
            </div>
          </div>

          <validation-observer
            ref="register-form"
            tag="form">
            <div class="form-group">
              <label for="name">{{ $t('user.firstName') }}</label>

              <validation-provider
                rules="required"
                name="name"
                v-slot="{ errors }">
                <f-input
                  v-model="name"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="surname">{{ $t('user.lastName') }}</label>

              <validation-provider
                rules="required"
                name="surname"
                v-slot="{ errors }">
                <f-input
                  v-model="surname"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="email">{{ $t('user.emailAddress') }}</label>

              <validation-provider
                rules="required|email"
                name="email"
                v-slot="{ errors }">
                <f-input
                  :disabled="registerFormInvitation"
                  v-model="email"
                  type="email"
                  :state="errors.length ? 'invalid' : null"/>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="phone">{{ $t('user.phoneNumber') }}</label>

              <validation-provider
                rules="required|min:9|tel"
                name="phone"
                mode="eager"
                v-slot="{ errors }">
                <vue-tel-input
                  ref="tel-input"
                  :key="defaultCountryCode"
                  :defaultCountry="defaultCountryCode"
                  :disabledFetchingCountry="true"
                  :dynamicPlaceholder="false"
                  :placeholder="''"
                  :validCharactersOnly="true"
                  :inputOptions="{
                    showDialCode:true
                  }"
                  :class="{
                    'f-input form-control': true,
                    'is-valid': !errors.length,
                    'is-invalid': errors.length
                  }"
                  v-model="phone"
                  :state="errors.length ? 'invalid' : null">
                </vue-tel-input>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <template v-if="isAdviserRegistration">
              <template v-if="langId === 2">
                <div class="form-group">
                  <label for="region">{{ $t('user.region') }}</label>
                  <validation-provider
                    tag="div"
                    rules="required"
                    name="region"
                    v-slot="{ errors }">
                    <div
                      :class="{
                        'form-select': true,
                        'is-invalid': errors.length
                      }">
                      <v-select
                        id="region"
                        :options="allRegions"
                        v-model="uk_region_id"
                        :reduce="(region) => region.id"
                        :clearable="false"
                        :searchable="false"
                        :placeholder="$t('user.selectRegion')"
                        label="name">
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="county">{{ $t('user.county') }}</label>
                  <validation-provider
                    tag="div"
                    rules="required"
                    name="county"
                    v-slot="{ errors }">
                    <div
                      :class="{
                        'form-select': true,
                        'is-invalid': errors.length
                      }">
                      <v-select
                        id="county"
                        :disabled="!uk_region_id"
                        :options="countiesForRegion"
                        v-model="uk_county_id"
                        :reduce="(county) => county.id"
                        :clearable="false"
                        :searchable="false"
                        :placeholder="$t('user.selectCounty')"
                        label="name">
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>
              </template>
              <template v-else>
                <div class="form-group">
                  <label for="province">{{ $t('user.province') }}</label>
                  <validation-provider
                    tag="div"
                    rules="required"
                    name="province"
                    v-slot="{ errors }">
                    <div
                      :class="{
                        'form-select': true,
                        'is-invalid': errors.length
                      }">
                      <v-select
                        id="province"
                        :options="allProvinces"
                        v-model="province_id"
                        :reduce="(province) => province.id"
                        :clearable="false"
                        :searchable="false"
                        :placeholder="$t('user.selectProvince')"
                        label="name">
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>
              </template>

              <div class="form-group">
                <label for="address">{{ $t('user.address') }}</label>

                <validation-provider
                  rules="required"
                  name="address"
                  v-slot="{ errors }">
                  <f-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="address"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="city">{{ $t('user.city') }}</label>

                <validation-provider
                  rules="required"
                  name="city"
                  v-slot="{ errors }">
                  <f-input
                    v-model="city"
                    :state="errors.length ? 'invalid' : null"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <template v-if="langId === 2">
                <div class="form-group">
                  <label for="postal-code-gb">{{ $t('user.postalCode') }}</label>

                  <validation-provider
                    rules="required|postal-code-gb"
                    name="postal-code-gb"
                    v-slot="{ errors }">
                    <f-input
                      v-model="post_code"
                      :state="errors.length ? 'invalid' : null"/>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="tin">{{ $t('user.taxIdentificationNumberGB') }}</label>

                  <validation-provider
                    rules="required"
                    name="tin"
                    v-slot="{ errors }">
                    <f-input-wrapper>
                      <f-input
                        v-model="nip"
                        :state="errors.length ? 'invalid' : null"/>
                    </f-input-wrapper>
                    <div class="additional-info">
                      <f-icon name="info" />
                      <span>{{ $t('validation.tin') }}</span>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>
              </template>
              <template v-else>
                <div class="form-group">
                  <label for="postal-code">{{ $t('user.postalCode') }}</label>

                  <validation-provider
                    rules="required|postal-code"
                    name="postal-code"
                    v-slot="{ errors }">
                    <f-input
                      v-model="post_code"
                      v-mask="'##-###'"
                      :state="errors.length ? 'invalid' : null"/>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="nip">{{ $t('user.taxIdentificationNumber') }}</label>

                  <validation-provider
                    rules="required|min:8|only-numbers"
                    name="nip"
                    v-slot="{ errors }">
                    <f-input
                      v-model="nip"
                      :state="errors.length ? 'invalid' : null"/>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>
              </template>

              <div class="form-group">
                <label for="adviser-status">{{ $t('user.status') }}</label>

                <validation-provider
                  rules="required"
                  name="adviser-status"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-select': true,
                      'is-invalid': errors.length
                    }">
                    <v-select
                      :options="adviserStatuses"
                      v-model="adviser_status"
                      :reduce="(status) => status.value"
                      :placeholder="$t('user.selectStatus')"
                      label="text">
                      <template slot="no-options">
                        {{ $t('ui.noResultsForGivenPhrase') }}
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="adviser-specialisation">{{ $t('user.specialization') }}</label>

                <validation-provider
                  rules="required"
                  name="adviser-specialisation"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-select': true,
                      'is-invalid': errors.length
                    }">
                    <v-select
                      :options="allSpecialisations"
                      v-model="adviser_specialisation_id"
                      :reduce="(specialisation) => specialisation.id"
                      :placeholder="$t('user.selectSpecialization')"
                      label="name">
                      <template slot="no-options">
                        {{ $t('ui.noResultsForGivenPhrase') }}
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group" v-if="langId !== 2">
                <label for="adviser-association">{{ $t('user.association') }}</label>
                <validation-provider
                  tag="div"
                  rules="required"
                  name="adviser-association"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-select': true,
                      'is-invalid': errors.length
                    }">
                    <v-select
                      id="adviser-association"
                      :options="allAssociations"
                      v-model="association_id"
                      :reduce="(association) => association.id"
                      :placeholder="$t('user.selectAssociation')"
                      label="name">
                      <template slot="no-options">
                        {{ $t('ui.noResultsForGivenPhrase') }}
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="association_id === 0"
                class="form-group">
                <label for="association-other">{{ $t('user.otherAssociation') }}</label>

                <validation-provider
                  rules="required"
                  name="association-other"
                  v-slot="{ errors }">
                  <f-input
                    v-model="association"
                    :state="errors.length ? 'invalid' : null"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </template>

            <div class="form-group">
              <label for="password">
                {{ $t('user.password') }}
              </label>

              <validation-provider
                rules="required|min:8"
                name="password"
                v-slot="{ errors }">
                <f-input
                  type="password"
                  :state="errors.length ? 'invalid' : null"
                  v-model="password" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="password-confirmation">
                {{ $t('user.repeatPassword') }}
              </label>

              <validation-provider
                rules="required|min:8|password:password"
                name="password-confirmation"
                v-slot="{ errors }">
                <f-input
                  type="password"
                  :state="errors.length ? 'invalid' : null"
                  v-model="password_confirmation" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group mb-2">
              <validation-provider
                rules="required|is-checked"
                name="accepted-regulations"
                v-slot="{ errors }">
                <f-checkbox
                  v-model="accepted_terms_and_policy_privacy"
                  :textLong="true"
                  :state="errors.length ? 'invalid' : null">
                  <span v-pure-html="$t('register.acceptTermsTxt', { regulationsUrl: regulationsUrl, privacyPolictUrl: privacyPolictUrl })"></span>
                </f-checkbox>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
            <div
              v-if="!isAdviserRegistration"
              class="form-group mb-2">
              <validation-provider
                rules=""
                name="contact-adviser-agreement"
                v-slot="{ errors }">
                <f-checkbox
                  v-model="contact_adviser_agreement"
                  :state="errors.length ? 'invalid' : null"
                  :textLong="true">
                  {{ $t('register.contactAdviserAgreementTxt') }}
                  <a
                    :title="langId === 2 ? $t('general.finPl') : $t('general.finhub')"
                    :href="siteDomain"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    class="text-medium-light">
                    {{ getHostname(siteDomain) }}
                  </a>.
                </f-checkbox>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
            <div
              v-if="!isAdviserRegistration"
              class="form-group mb-2">
              <validation-provider
                rules=""
                name="contact-finhub-agreementt"
                v-slot="{ errors }">
                <f-checkbox
                  v-model="contact_finhub_agreement"
                  :textLong="true"
                  :state="errors.length ? 'invalid' : null">
                  {{ $t('register.contactFinhubAgreementTxt') }}
                  <a
                    :title="langId === 2 ? $t('general.finPl') : $t('general.finhub')"
                    :href="siteDomain"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    class="text-medium-light">
                    {{ getHostname(siteDomain) }}
                  </a>.
                </f-checkbox>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <p
              v-if="!isAdviserRegistration"
              class="text-medium-light line-height-base mb-3"
              v-pure-html="$t('register.dataProcessingAgreementTxt', { regulationsUrl: siteDomain + pageLinks.regulations, privacyPolicyUrl: siteDomain + pageLinks.privacyPolicy })">
            </p>

            <f-button
              type="submit"
              theme="primary"
              class="full-width mt-1"
              big
              withArrow
              @click.prevent="validateForm"
              :disabled="submitting">
              <template v-if="!submitting">{{ $t('register.register') }}</template>
              <template v-else>{{ $t('register.registration') }}&hellip;</template>
            </f-button>

            <div class="text-center mt-4">
              <router-link
                to="/logowanie"
                class="lnk lnk-big lnk-big-blue"
                :disabled="submitting">
                <template v-if="isAdviserRegistration">{{ $t('ui.goToLoginPage') }}</template>
                <template v-else>{{ $t('ui.backToLoginPage') }}</template>
              </router-link>
            </div>
          </validation-observer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import getPageLinks from '@/data/page-links';
import AdviserStatuses from '@/data/adviser-statuses.js';

export default {
  name: 'Register',
  computed: {
    ...mapGetters({
      siteDomain: 'getSideDomain',
      langId: 'getUserLanguageId'
    }),
    regionDataLoaded () {
      if (this.langId === 2) {
        return this.allRegionsLoaded;
      }

      return this.allProvincesLoaded;
    },
    allDataLoaded () {
      if (!this.isAdviserRegistration) {
        return this.dataFetching && !!this.defaultCountryCode;
      }

      return this.dataFetching && this.regionDataLoaded && this.allSpecialisationsLoaded && this.allAssociationsLoaded && this.associationsSet && !!this.defaultCountryCode;
    },
    isAdviserRegistration () {
      if (!this.$route.meta || !this.$route.meta.isAdviser) {
        return false;
      }

      return !!this.$route.meta.isAdviser;
    },
    regulationsUrl () {
      if (this.isAdviserRegistration) {
        return this.siteDomain + this.pageLinks.regulationsForExpert;
      }

      return this.siteDomain + this.pageLinks.regulations;
    },
    privacyPolictUrl () {
      if (this.isAdviserRegistration) {
        return this.siteDomain + this.pageLinks.privacyPolicyForExpert;
      }

      return this.siteDomain + this.pageLinks.privacyPolicy;
    },
    adviserStatuses () {
      return AdviserStatuses;
    },
    isLanguageSet () {
      if (this.$store.getters['getUserHasLanguage']) {
        return true;
      }

      return false;
    },
    isLanguageError () {
      if (this.$store.getters['getUserLanguageHasError']) {
        return true;
      }

      return false;
    },
    countiesForRegion () {
      if (!this.uk_region_id) {
        return [];
      }

      let selectedRegion = this.allRegions.find(item => item.id === this.uk_region_id);
      if (!selectedRegion) {
        return [];
      }

      return selectedRegion.counties;
    }
  },
  watch: {
    isLanguageError (newValue) {
      if (!newValue) {
        return;
      }
      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountryCode', this.$store.getters['getUserPhoneCountryCode']);
      })
    },
    isLanguageSet (newValue) {
      if (!newValue) {
        return;
      }

      Vue.nextTick(() => {
        Vue.set(this, 'defaultCountryCode', this.$store.getters['getUserPhoneCountryCode']);

        if (this.$route.meta && this.$route.meta.isAdviser && !this.dataFetching) {
          this.loadAdditionalData();
        }
      })
    },
    uk_region_id () {
      this.uk_county_id = 0;
    }
  },
  data () {
    return {
      accepted_terms_and_policy_privacy: false,
      address: '',
      adviser_specialisation_id: null,
      adviser_status: null,
      allAssociations: [{
        id: 0,
        name: this.$t('user.other')
      }],
      allAssociationsLoaded: false,
      allProvinces: [],
      allProvincesLoaded: false,
      allRegions: [],
      allRegionsLoaded: false,
      allSpecialisations: [],
      allSpecialisationsLoaded: false,
      association: '',
      association_id: null,
      associationsSet: false,
      authError: false,
      city: '',
      contact_adviser_agreement: false,
      contact_finhub_agreement: false,
      currentErrors: null,
      defaultCountryCode: null,
      email: '',
      dataFetching: false,
      name: '',
      nip: '',
      pageLinks: getPageLinks(),
      password: '',
      password_confirmation: '',
      phone: null,
      post_code: '',
      province_id: null,
      registerFormInvitation: false,
      submitting: false,
      surname: '',
      uk_county_id: null,
      uk_region_id: null
    }
  },
  mounted () {
    if ((this.$store.getters['getUserHasLanguage'] || this.$store.getters['getUserLanguageHasError']) && !this.defaultCountryCode) {
      Vue.set(this, 'defaultCountryCode', this.$store.getters['getUserPhoneCountryCode']);
    }

    if (this.$route.params && this.$route.params.token) {
      this.registerFormInvitation = true;
      delete this.$http.defaults.headers['X-Csrf-Token'];

      this.$http.post('/api/auth/get-data-by-invitation-token', this.$route.params).then((response) => {
        this.name = response.data.data.name;
        this.surname = response.data.data.surname;
        this.email = response.data.data.email;
        this.phone = response.data.data.phone;
        this.dataFetching = true;
      }).catch((error) => {
        console.log(error);
      });
    } else {
      if (!(this.$route.meta && this.$route.meta.isAdviser)) {
        this.dataFetching = true;
      }

      if (this.isLanguageSet) {
        this.loadAdditionalData();
      }
    }
  },
  methods: {
    loadAdditionalDataPl () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/provinces/items',
        method: 'get',
        outputKey: 'allProvinces',
        loadedKey: 'allProvincesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    loadAdditionalDataGb () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/uk/regions/items',
        method: 'get',
        outputKey: 'allRegions',
        loadedKey: 'allRegionsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    loadAdditionalData () {
      this.dataFetching = true;

      if (this.langId === 2) {
        this.loadAdditionalDataGb();
      } else {
        this.loadAdditionalDataPl();
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/specialisations/items',
        method: 'get',
        outputKey: 'allSpecialisations',
        loadedKey: 'allSpecialisationsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/associations/items',
        method: 'get',
        loadedKey: 'allAssociationsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        },
        successAction: (response) => {
          this.allAssociations = this.allAssociations.concat(response);
          Vue.nextTick(() => {
            this.associationsSet = true;
          })
        }
      });
    },
    register () {
      this.authError = false;
      this.submitting = true;
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isAdviserRegistration ? '/api/auth/register-adviser' : '/api/auth/register';

      this.$http.post(endpointUrl, formDataToSend).then((response) => {
        this.submitting = false;

        if (('success' in response.data) && response.data.success === 0) {
          this.authError = true;
          if (response.data.message) {
            this.$el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
            this.currentErrors = [];
            this.currentErrors.push({ error: response.data.message });
          }

          return;
        }

        Vue.nextTick(() => {
          if (this.isAdviserRegistration) {
            this.sendFbLeadEvent();
          }
          if (('data' in response.data) && ('sent_activation_email' in response.data.data) && response.data.data.sent_activation_email) {
            this.$router.push('/rejestracja-wiadomosc-wyslana');
            return;
          }

          this.$router.push('/logowanie/udana-rejestracja');
        });
      }).catch((error) => {
        console.log(error);
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        this.currentErrors = error.response.data.errors;
        this.submitting = false;
        this.authError = true;
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['register-form'], this.register);
    },
    prepareFormDataToSend () {
      let telInput = this.$refs['tel-input'];

      let formDataToSend = {
        name: this.name,
        surname: this.surname,
        email: this.email,
        phone: telInput ? telInput.phoneObject.number.international : this.phone,
        password: this.password,
        password_confirmation: this.password_confirmation
      };

      if (this.isAdviserRegistration) {
        formDataToSend.nip = this.nip;
        formDataToSend.city = this.city;
        formDataToSend.post_code = this.post_code;
        formDataToSend.province_id = this.province_id;
        formDataToSend.uk_county_id = this.uk_county_id;
        formDataToSend.uk_region_id = this.uk_region_id;
        formDataToSend.address = this.address;
        formDataToSend.adviser_status = this.adviser_status;
        formDataToSend.association = '';
        formDataToSend.association_id = this.association_id;
        formDataToSend.adviser_specialisation_id = this.adviser_specialisation_id;
        formDataToSend.accepted_terms_and_policy_privacy = this.accepted_terms_and_policy_privacy ? 1 : 0;

        if (!this.association_id) {
          formDataToSend.association = this.association;
        }
      } else {
        formDataToSend.accepted_terms_and_policy_privacy = this.accepted_terms_and_policy_privacy ? 1 : 0;
        formDataToSend.contact_adviser_agreement = this.contact_adviser_agreement ? 1 : 0;
        formDataToSend.contact_finhub_agreement = this.contact_finhub_agreement ? 1 : 0;
      }

      if (this.registerFormInvitation) {
        formDataToSend.customer_invitation_token = this.$route.params.token;
      }

      return formDataToSend;
    },
    getHostname (url) {
      return new URL(url).hostname;
    },
    sendFbLeadEvent () {
      if (!window.Cookiebot || !window.Cookiebot.consent.marketing) {
        return;
      }

      if (window.fbq) {
        window.fbq('track', 'Lead');
      }
    }
  }
}
</script>

<style scoped>
.error-item:last-child {
  margin-bottom: 0!important;
}
</style>
